import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBookOpen, FiPlay, FiTrendingUp, FiClock, FiStar, FiTarget, FiSearch, FiX, FiZap } = FiIcons;

const StudyRoom = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Get search query from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (q) {
      setSearchQuery(q);
    }
  }, [location.search]);

  const subjects = [
    {
      name: 'Mathematics',
      description: 'Master algebra, geometry, calculus and more',
      icon: FiTrendingUp,
      color: 'bg-blue-500',
      path: '/quiz/mathematics',
      progress: 75,
      totalQuizzes: 45,
      completedQuizzes: 34,
      tags: ['algebra', 'geometry', 'calculus', 'statistics']
    },
    {
      name: 'Science',
      description: 'Explore physics, chemistry, and biology',
      icon: FiBookOpen,
      color: 'bg-green-500',
      path: '/quiz/science',
      progress: 60,
      totalQuizzes: 38,
      completedQuizzes: 23,
      tags: ['physics', 'chemistry', 'biology', 'experiments']
    },
    {
      name: 'English Literature',
      description: 'Improve your language and writing skills',
      icon: FiBookOpen,
      color: 'bg-purple-500',
      path: '/quiz/english',
      progress: 85,
      totalQuizzes: 32,
      completedQuizzes: 27,
      tags: ['grammar', 'literature', 'writing', 'vocabulary']
    },
    {
      name: 'History',
      description: 'Learn about past events and civilizations',
      icon: FiClock,
      color: 'bg-orange-500',
      path: '/quiz/history',
      progress: 45,
      totalQuizzes: 28,
      completedQuizzes: 13,
      tags: ['world history', 'african history', 'ancient history']
    },
    {
      name: 'Geography',
      description: 'Discover the world and its features',
      icon: FiTarget,
      color: 'bg-teal-500',
      path: '/quiz/geography',
      progress: 55,
      totalQuizzes: 35,
      completedQuizzes: 19,
      tags: ['physical geography', 'countries', 'climate', 'maps']
    },
    {
      name: 'Leadership Skills',
      description: 'Develop your leadership and communication abilities',
      icon: FiStar,
      color: 'bg-yellow-500',
      path: '/quiz/leadership',
      progress: 70,
      totalQuizzes: 25,
      completedQuizzes: 18,
      tags: ['communication', 'teamwork', 'management', 'motivation']
    },
    {
      name: 'Puzzles & Brain Teasers',
      description: 'Challenge your mind with logic puzzles and brain teasers',
      icon: FiZap,
      color: 'bg-pink-500',
      path: '/quiz/puzzles',
      progress: 30,
      totalQuizzes: 20,
      completedQuizzes: 6,
      tags: ['logic', 'riddles', 'critical thinking', 'problem solving']
    }
  ];

  // Filter subjects based on search query
  const filteredSubjects = subjects.filter(subject => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      subject.name.toLowerCase().includes(search) ||
      subject.description.toLowerCase().includes(search) ||
      subject.tags?.some(tag => tag.toLowerCase().includes(search))
    );
  });

  const handleQuizStart = (subject) => {
    addNotification({
      type: 'info',
      title: 'Starting Quiz',
      message: `Loading ${subject.name} quiz...`
    });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Update URL with search query
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set('q', value);
    } else {
      params.delete('q');
    }
    const newUrl = `${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    navigate(newUrl, { replace: true });
  };

  const clearSearch = () => {
    setSearchQuery('');
    navigate('/study-room', { replace: true });
  };

  return (
    <div className="space-y-4 px-4 py-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-4 md:p-6 border border-gray-100"
      >
        <div className="text-center">
          <SafeIcon icon={FiBookOpen} className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 text-primary-600" />
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Study Room</h1>
          <p className="text-gray-600 text-sm md:text-base">
            Choose a subject to start your learning journey. Track your progress and master new skills!
          </p>
        </div>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg p-4 border border-gray-100"
      >
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search subjects, topics, or quizzes..."
            className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-2"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          )}
        </div>
        
        {/* Search Results Info */}
        {searchQuery && (
          <div className="mt-3 text-sm text-gray-600">
            {filteredSubjects.length > 0 ? (
              <>Found {filteredSubjects.length} subject{filteredSubjects.length !== 1 ? 's' : ''} matching "{searchQuery}"</>
            ) : (
              <>No subjects found matching "{searchQuery}"</>
            )}
          </div>
        )}
      </motion.div>

      {/* Progress Overview - Only show if no search or search has results */}
      {(!searchQuery || filteredSubjects.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl shadow-lg p-4 md:p-6 text-white"
        >
          <div className="text-center">
            <SafeIcon icon={FiTrendingUp} className="w-8 h-8 md:w-10 md:h-10 mx-auto mb-3 md:mb-4 animate-bounce-gentle" />
            <h2 className="text-xl md:text-2xl font-bold mb-2">Your Progress</h2>
            <p className="text-primary-100 mb-4 text-sm md:text-base">
              Keep up the excellent work! You're making great progress on your learning journey.
            </p>
            <div className="grid grid-cols-3 gap-3 md:gap-4">
              <div className="bg-white bg-opacity-20 rounded-lg p-3 md:p-4">
                <div className="text-lg md:text-2xl font-bold">{user?.points || 1250}</div>
                <div className="text-xs md:text-sm">Total Points</div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-3 md:p-4">
                <div className="text-lg md:text-2xl font-bold">47</div>
                <div className="text-xs md:text-sm">Quizzes Done</div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-3 md:p-4">
                <div className="text-lg md:text-2xl font-bold">7</div>
                <div className="text-xs md:text-sm">Subjects</div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Subjects Grid */}
      {filteredSubjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filteredSubjects.map((subject, index) => (
            <motion.div
              key={subject.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              {/* Subject Header */}
              <div className={`${subject.color} p-4 md:p-5 text-white relative overflow-hidden`}>
                <div className="absolute top-0 right-0 w-16 h-16 bg-white bg-opacity-10 rounded-full -mr-8 -mt-8"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <SafeIcon icon={subject.icon} className="w-6 h-6 md:w-8 md:h-8" />
                    <span className="text-xs md:text-sm bg-white bg-opacity-20 px-2 py-1 rounded-full">
                      {subject.progress}%
                    </span>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold mb-1">{subject.name}</h3>
                  <p className="text-xs md:text-sm opacity-90 line-clamp-2">{subject.description}</p>
                </div>
              </div>

              {/* Subject Content */}
              <div className="p-4 md:p-5">
                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs md:text-sm text-gray-600 mb-2">
                    <span>Progress</span>
                    <span>{subject.completedQuizzes}/{subject.totalQuizzes} quizzes</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-primary-500 to-accent-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${subject.progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Tags */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1 md:gap-2">
                    {subject.tags.slice(0, 3).map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                    {subject.tags.length > 3 && (
                      <span className="text-gray-500 text-xs">+{subject.tags.length - 3} more</span>
                    )}
                  </div>
                </div>

                {/* Action Button */}
                <Link
                  to={subject.path}
                  onClick={() => handleQuizStart(subject)}
                  className="w-full bg-gradient-to-r from-primary-500 to-accent-500 text-white py-3 px-4 rounded-lg font-medium hover:from-primary-600 hover:to-accent-600 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiPlay} className="w-4 h-4" />
                  <span>Start Quiz</span>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      ) : searchQuery ? (
        /* No Results */
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 text-center"
        >
          <SafeIcon icon={FiSearch} className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No subjects found</h3>
          <p className="text-gray-600 mb-4">
            We couldn't find any subjects matching "<strong>{searchQuery}</strong>". Try different keywords or browse all subjects.
          </p>
          <button
            onClick={clearSearch}
            className="bg-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors"
          >
            Clear Search
          </button>
        </motion.div>
      ) : null}
    </div>
  );
};

export default StudyRoom;